import React, {Component} from 'react'
import PropTypes from 'prop-types'
import {isEmpty, values, flowRight as compose, get} from 'lodash'
import Grid from '@mui/material/Grid'
import Input from '@mui/material/Input'
import {frontends} from '@bdswiss/common-enums'
import {withNamespaces, Trans} from 'react-i18next'
import withStyles from '@mui/styles/withStyles'
import InputLabel from '@mui/material/InputLabel'
import Typography from '@mui/material/Typography'
import FormControl from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'
import InputAdornment from '@mui/material/InputAdornment'
import CreditCardIcon from '@mui/icons-material/CreditCardRounded'
import SelfPostingForm from './SelfPostingForm'
import {withCreateDeposit, PaymentActionButton} from './helpers'
import messages from '../../../../assets/messages'
import {getPlatform, getSourceBasedOnLocationPrevPath, logEventCustomParams} from '../../../../common/utils'
import {validateNumber} from '../../../../common/utils/validations'

const validators = {
  cardNumber: (value) => {
    if (!value) return messages.requiredField
    if (value.length < 12 || !validateNumber(value)) return messages.invalidCardNumber
  }
}

const style = (theme) => ({
  formControl: {
    margin: theme.spacing(1)
  },
  error: {
    color: theme.palette.error.main
  },
})

class FuntoPayProvider extends Component {

  state = {form:{cardNumber: ''}, errors:{}, doSubmitForm: false}

  validateFields() {
    const {form} = this.state
    const errors = {}
    Object.keys(form).forEach((k) => {
      const validate = validators[k]
      errors[k] = validate && validate(form[k])
    })
    this.setState({errors})
  }

  formIsValid() {
    const {form, errors} = this.state
    return isEmpty(values(errors).filter((v) => v)) && !isEmpty(values(form).filter((v) => v))
  }

  handleChange(field, value) {
    const {form} = this.state
    this.setState({
      form: {
        ...form,
        [field]: value
      }
    }, () => this.validateFields())
  }

  doCreateDeposit() {
    if (!this.formIsValid()) return

    const {providerProperties: {provider, id: paymentOptionId}, account, amount, onError, useVirtualPaymentPage, bonusAmount, bonusTerms,
      history: {location}} = this.props
    const {form: {cardNumber}} = this.state

    this.setState({creatingDeposit: true})

    const firebaseParams = {
      source: getSourceBasedOnLocationPrevPath(get(location, 'state', {prevPath: get(location, 'pathname')})),
      login: account.id,
      currency: account.currency,
      vendor: provider,
      amount,
    }
    logEventCustomParams('depositAttempted', firebaseParams)

    const variables = {
      accountId: account.id,
      amount,
      vendor: provider,
      paymentOptionId,
      frontend: frontends.web2.value,
      args: JSON.stringify({
        platform: getPlatform(),
        cardNumber,
        useVirtualPaymentPage,
        bonusAmount,
        bonusTerms,
      }),
    }

    this.props.createDepositRequest({variables})
      .then(({data: {newDeposit}}) => {
        const {payment: {url}} = newDeposit
        this.setState({formData: JSON.parse(url), creatingDeposit: false, doSubmitForm: true})
      })
      .catch((e) => {
        this.setState({creatingDeposit: false})
        onError && onError(e)
      })
  }

  render() {
    const {onSubmit, classes, canProceed} = this.props
    const {errors, formData, doSubmitForm, creatingDeposit} = this.state

    return (
      <React.Fragment>
        <Grid container direction="row" spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h5" gutterBottom>
              <Trans {...messages.enterPaymentDetails} />
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <FormControl variant="standard" className={classes.formControl} fullWidth>
              <InputLabel htmlFor="cardNumber">
                <Trans {...messages.cardNumber} />
              </InputLabel>
              <Input
                id="cardNumber"
                name="cardNumber"
                type="text"
                error={!!errors.cardNumber}
                onChange={(e) => this.handleChange('cardNumber', e.target.value)}
                inputProps={{pattern: '[0-9]*', inputMode:'numeric'}}
                startAdornment={
                  <InputAdornment position="start" >
                    <CreditCardIcon />
                  </InputAdornment>
                }
              />
              {
                errors.cardNumber &&
                <FormHelperText className={classes.error}><Trans {...errors.cardNumber} /></FormHelperText>
              }
            </FormControl>
          </Grid>
          <PaymentActionButton
            loading={creatingDeposit}
            disable={canProceed || !this.formIsValid()}
            onClick={() => this.doCreateDeposit()}
          />
          <SelfPostingForm formData={formData} doSubmitForm={doSubmitForm} onSubmit={onSubmit}/>
        </Grid>

      </React.Fragment>
    )
  }
}

FuntoPayProvider.propTypes = {
  account: PropTypes.shape({
    id: PropTypes.number.isRequired,
    currency: PropTypes.string.isRequired,
  }).isRequired,
  amount: PropTypes.number.isRequired,
  providerProperties: PropTypes.shape({
    name: PropTypes.string.isRequired,
    provider: PropTypes.string.isRequired,
    paymentKey: PropTypes.string.isRequired,
    paymentOptionId: PropTypes.number.isRequired,
  }).isRequired,
  onError: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
}

export default compose(
  withNamespaces(),
  withStyles(style),
  withCreateDeposit
)(FuntoPayProvider)
